import { z } from "zod"
import { reportTypeEnum } from "../db/schema"
import type { ReportType } from "../util/prompts"
import { asConst } from "../util/typed-helpers"
import {
  REPORT_DEPENDENCY_GRAPH,
  STANDARD_REPORT_EXECUTION_ORDER as SHARED_STANDARD_REPORT_EXECUTION_ORDER,
} from "./report-generation-config"

// Enum values as object for dot-notation access
export const ReportTypes = asConst(reportTypeEnum.enumValues)

export const SUMMARY_REPORT_TYPE = ReportTypes.ESTATE_SUMMARY

export type StandardReportType = (typeof SHARED_STANDARD_REPORT_EXECUTION_ORDER)[number]

export const STANDARD_REPORT_TYPES: readonly StandardReportType[] =
  SHARED_STANDARD_REPORT_EXECUTION_ORDER

for (const type of STANDARD_REPORT_TYPES) {
  if (!(type in ReportTypes)) {
    throw new Error(`Report type "${type}" is not present in the database enum`)
  }
}

// Why: Step Functions payloads are validated against Zod schema using this allow-list of literals.
export const ALLOWED_REPORT_TYPES = [
  ...STANDARD_REPORT_TYPES,
  ReportTypes.WILL_REVIEW,
  ReportTypes.INSURANCE_REVIEW,
  ReportTypes.TRUST_REVIEW,
] as const

export const ALL_REPORT_TYPES = [...ALLOWED_REPORT_TYPES, SUMMARY_REPORT_TYPE]

export const ALLOWED_REPORT_TYPES_TUPLE = [...ALLOWED_REPORT_TYPES] as unknown as [
  (typeof ALLOWED_REPORT_TYPES)[number],
  ...(typeof ALLOWED_REPORT_TYPES)[number][],
]

type DependencyMap = Readonly<Record<StandardReportType, readonly StandardReportType[]>>

// Why: Centralise the dependency graph so orchestration, API, and runtime helpers stay in sync.
export const REPORT_DEPENDENCIES: DependencyMap = REPORT_DEPENDENCY_GRAPH

function assertNoUnknownDependencies(map: DependencyMap) {
  const known = new Set<StandardReportType>(STANDARD_REPORT_TYPES)
  for (const [reportType, deps] of Object.entries(map) as Array<
    [StandardReportType, readonly StandardReportType[]]
  >) {
    for (const dep of deps) {
      if (!known.has(dep)) {
        throw new Error(`Unknown dependency "${dep}" referenced by ${reportType}`)
      }
    }
  }
}

function computeTopologicalOrder(map: DependencyMap): StandardReportType[] {
  assertNoUnknownDependencies(map)
  const inDegree = new Map<StandardReportType, number>()
  const dependents = new Map<StandardReportType, StandardReportType[]>()
  const baseOrderIndex = new Map<StandardReportType, number>()

  STANDARD_REPORT_TYPES.forEach((type, index) => {
    inDegree.set(type, 0)
    dependents.set(type, [])
    baseOrderIndex.set(type, index)
  })

  for (const [type, deps] of Object.entries(map) as Array<
    [StandardReportType, readonly StandardReportType[]]
  >) {
    for (const dep of deps) {
      inDegree.set(type, (inDegree.get(type) ?? 0) + 1)
      dependents.get(dep)?.push(type)
    }
  }

  const queue = STANDARD_REPORT_TYPES.filter((type) => (inDegree.get(type) ?? 0) === 0)
  const order: StandardReportType[] = []

  while (queue.length > 0) {
    queue.sort((a, b) => (baseOrderIndex.get(a) ?? 0) - (baseOrderIndex.get(b) ?? 0))
    const current = queue.shift()
    if (!current) break
    order.push(current)
    for (const dependent of dependents.get(current) ?? []) {
      const remaining = (inDegree.get(dependent) ?? 0) - 1
      inDegree.set(dependent, remaining)
      if (remaining === 0) {
        queue.push(dependent)
      }
    }
  }

  if (order.length !== STANDARD_REPORT_TYPES.length) {
    throw new Error("Cycle detected in report dependency graph")
  }

  return order
}

const computedExecutionOrder = computeTopologicalOrder(REPORT_DEPENDENCIES)
if (computedExecutionOrder.join("|") !== SHARED_STANDARD_REPORT_EXECUTION_ORDER.join("|")) {
  throw new Error("Shared report execution order does not match dependency graph")
}
export const STANDARD_REPORT_EXECUTION_ORDER = computedExecutionOrder

const STANDARD_REPORT_SET = new Set(STANDARD_REPORT_TYPES)
const ALLOWED_REPORT_TYPES_SET = new Set(ALLOWED_REPORT_TYPES)

type AllowedReportType = (typeof ALLOWED_REPORT_TYPES)[number]

function assertAllowedReportType(value: unknown): asserts value is AllowedReportType {
  if (typeof value !== "string" || !ALLOWED_REPORT_TYPES_SET.has(value as AllowedReportType)) {
    throw new Error(`Unknown report type: ${String(value)}`)
  }
}

function gatherDependencies(
  type: StandardReportType,
  map: DependencyMap,
  acc: Set<StandardReportType>,
) {
  for (const dep of map[type] ?? []) {
    if (!acc.has(dep)) {
      acc.add(dep)
      gatherDependencies(dep, map, acc)
    }
  }
}

export interface ExpandReportsOptions {
  includeDependenciesForRequested?: boolean
}

export interface ExpandReportsResult {
  /**
   * Standard reports sorted in deterministic dependency order.
   * Used by CDK to build the sequential Step Functions chain.
   */
  orderedStandardReports: readonly StandardReportType[]
  /**
   * Reports that should be invoked for this execution (standard + non-standard).
   * The array is dependency-ordered for standard reports.
   */
  reportsToExecute: readonly ReportType[]
  /**
   * Standard reports explicitly requested by the caller (pre-expansion).
   */
  requestedStandardReports: readonly StandardReportType[]
  /**
   * Non-standard reports requested (e.g. WILL_REVIEW).
   */
  requestedNonStandardReports: readonly Exclude<ReportType, StandardReportType>[]
}

export function expandReportsWithDependencies(
  requested: readonly ReportType[] | undefined | null,
  options?: ExpandReportsOptions,
): ExpandReportsResult {
  const includeDependencies = options?.includeDependenciesForRequested ?? true
  const requestedStandard = new Set<StandardReportType>()
  const requestedNonStandard = new Set<Exclude<ReportType, StandardReportType>>()

  if (!requested || requested.length === 0) {
    STANDARD_REPORT_EXECUTION_ORDER.forEach((type) => {
      requestedStandard.add(type)
    })
  } else {
    for (const value of requested) {
      assertAllowedReportType(value)
      if (STANDARD_REPORT_SET.has(value as StandardReportType)) {
        requestedStandard.add(value as StandardReportType)
      } else {
        requestedNonStandard.add(value as Exclude<ReportType, StandardReportType>)
      }
    }
  }

  if (includeDependencies) {
    const queue = Array.from(requestedStandard)
    while (queue.length > 0) {
      const current = queue.pop() as StandardReportType
      for (const dep of REPORT_DEPENDENCIES[current] ?? []) {
        if (!requestedStandard.has(dep)) {
          requestedStandard.add(dep)
          queue.push(dep)
        }
      }
    }
  }

  const orderedStandardReports = STANDARD_REPORT_EXECUTION_ORDER
  const reportsToExecute: ReportType[] = []

  for (const type of orderedStandardReports) {
    if (requestedStandard.has(type)) {
      reportsToExecute.push(type)
    }
  }

  for (const nonStandard of requestedNonStandard) {
    reportsToExecute.push(nonStandard)
  }

  return {
    orderedStandardReports,
    reportsToExecute,
    requestedStandardReports: Array.from(requestedStandard),
    requestedNonStandardReports: Array.from(requestedNonStandard),
  }
}

export function getTransitiveDependencies(reportType: ReportType): readonly StandardReportType[] {
  if (!STANDARD_REPORT_SET.has(reportType as StandardReportType)) {
    return []
  }

  const acc = new Set<StandardReportType>()
  gatherDependencies(reportType as StandardReportType, REPORT_DEPENDENCIES, acc)

  return STANDARD_REPORT_EXECUTION_ORDER.filter((type) => acc.has(type))
}

// Why: Step Functions payloads are validated against Zod schema using this allow-list of literals.
export type SummaryReportType = typeof SUMMARY_REPORT_TYPE
export type AnyReportType = ReportType | SummaryReportType

export const zGenerateSingleReportEvent = z.object({
  estateId: z.string(),
  userId: z.string(),
  reportType: z.enum(ALLOWED_REPORT_TYPES_TUPLE),
  jurisdiction: z.string().optional(),
  additionalPrompt: z.string().optional(),
})

export type GenerateSingleReportEvent = z.infer<typeof zGenerateSingleReportEvent>

export function parseGenerateSingleReportEvent(event: unknown): GenerateSingleReportEvent {
  return zGenerateSingleReportEvent.parse(event)
}
